import React, { useEffect, useRef, useState } from 'react';

import { Loader2, Pause, Play, RotateCcw, Volume2 } from 'lucide-react';

import { fetchRecording } from '../api';

const formatAudioTime = (seconds: number): string => {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

interface RecordingPlayerProps {
  recordingUrl?: string;
}

const RecordingPlayer: React.FC<RecordingPlayerProps> = ({ recordingUrl }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  if (!recordingUrl) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
        <Volume2 size={14} className="text-gray-300" />
        Sin grabación
      </span>
    );
  }

  const loadAudio = async (): Promise<string | null> => {
    if (blobUrl) return blobUrl;
    setIsLoading(true);
    setHasError(false);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const recordingId = recordingUrl.split('/recording/')[1];
      const blob = await fetchRecording(recordingId, controller.signal);
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setBlobUrl(url);
      return url;
    } catch (err) {
      if (!controller.signal.aborted) {
        setHasError(true);
      }
      return null;
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  };

  const handlePlayClick = async (): Promise<void> => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    let url = blobUrl;
    if (!url) {
      url = await loadAudio();
      if (!url) return;
    }

    const audio = audioRef.current;
    if (audio) {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = (): void => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    setCurrentTime(audio.currentTime);
    setProgress((audio.currentTime / audio.duration) * 100);
  };

  const handleLoadedMetadata = (): void => {
    const audio = audioRef.current;
    if (audio) setDuration(audio.duration);
  };

  const handleEnded = (): void => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>): void => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audio.currentTime = pct * duration;
  };

  if (hasError && !blobUrl) {
    return (
      <button
        type="button"
        onClick={loadAudio}
        className="hover:text-red-700 inline-flex items-center gap-1 text-xs text-red-500 transition-colors"
      >
        <RotateCcw size={14} />
        Reintentar
      </button>
    );
  }

  if (!blobUrl && !isLoading) {
    return (
      <button
        type="button"
        onClick={handlePlayClick}
        title="Escuchar grabación"
        className="bg-blue-50 hover:bg-blue-100 hover:text-blue-700 w-7 h-7 flex items-center justify-center flex-shrink-0 text-blue-600 transition-colors rounded-full"
      >
        <Play size={14} className="ml-0.5" />
      </button>
    );
  }

  if (isLoading) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-blue-500">
        <Loader2 size={14} className="animate-spin" />
        Cargando...
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2 min-w-[160px]">
      {blobUrl && (
        <audio
          ref={audioRef}
          src={blobUrl}
          preload="metadata"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
        />
      )}
      <button
        type="button"
        onClick={handlePlayClick}
        title={isPlaying ? 'Pausar grabación' : 'Escuchar grabación'}
        className="bg-blue-50 hover:bg-blue-100 hover:text-blue-700 w-7 h-7 flex items-center justify-center flex-shrink-0 text-blue-600 transition-colors rounded-full"
      >
        {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-[80px]">
        <div
          className="h-1.5 cursor-pointer rounded-full bg-gray-200"
          onClick={handleSeek}
          role="slider"
          tabIndex={0}
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Escuchar grabación"
        >
          <div
            className="h-full transition-all duration-100 bg-blue-500 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-0.5">
          <span className="text-[10px] text-gray-400">{formatAudioTime(currentTime)}</span>
          <span className="text-[10px] text-gray-400">{formatAudioTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

export default RecordingPlayer;
